import { useRef, useState } from "react";
import { ExternalLink, ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { fmtMoney } from "../../../lib/format";
import { useApi } from "../../customer/hooks";
import { PageHead, chip } from "../../../components/common/Page";
import { SkeletonBlock } from "../../../components/common/EmptyState";

export const PhotoUpload = ({ code, onDone, compact }) => {
  const { t } = useI18n();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/admin/catalogue/items/${encodeURIComponent(code)}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(t("x.photo.uploaded")); onDone?.(); }
    catch (e) { toast.error(e?.response?.data?.detail === "invalid_image" ? t("x.photo.invalid") : t("common.error")); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`photo-file-${code}`} />
      <button type="button" disabled={busy} onClick={() => ref.current?.click()} data-testid={`photo-upload-${code}`}
        className={`flex items-center justify-center gap-1.5 rounded-full bg-navy text-xs font-semibold text-white disabled:opacity-60 ${compact ? "h-9 w-9" : "h-10 px-3"}`} aria-label={t("x.photo.upload")}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}{!compact && t("x.photo.upload")}
      </button>
    </>
  );
};

export default function NoPhotoReport() {
  const { t, lang } = useI18n();
  const [months, setMonths] = useState(12);
  const rep = useApi("noPhoto", "/admin/catalogue/no-photo", { months, limit: 200 }, { staleTime: 0 });
  const rows = rep.data?.items || [];
  return (
    <div className="space-y-4" data-testid="no-photo-page">
      <PageHead title={t("x.photo.title")} sub={t("x.photo.sub", { n: rep.data?.total ?? "…", s: rep.data?.with_sales ?? "…" })} />
      <div className="flex gap-2">{[3, 6, 12, 24].map((m) => <button key={m} type="button" className={chip(months === m)} onClick={() => setMonths(m)} data-testid={`no-photo-months-${m}`}>{t("x.photo.months", { n: m })}</button>)}</div>
      {rep.isLoading ? <SkeletonBlock className="h-64" /> : (
        <ul className="divide-y divide-slate-100 overflow-hidden rounded-2xl bg-white shadow-sm" data-testid="no-photo-list">
          {rows.map((r, idx) => (
            <li key={r.code} className="flex items-center gap-3 px-4 py-2.5" data-testid={`no-photo-${r.code}`}>
              <span className="w-6 shrink-0 text-right text-xs tabular-nums text-slate-400">{idx + 1}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-navy">{r[`name_${lang}`] || r.name_el}</p>
                <p className="truncate text-xs text-slate-500">{r.code}{r.brand ? ` · ${r.brand}` : ""} · {r.family || "—"}</p>
              </div>
              <div className="shrink-0 text-right text-xs"><p className="font-semibold tabular-nums text-navy">{fmtMoney(r.net)}</p><p className="text-slate-500">{r.qty} {t("x.photo.pcs")}</p></div>
              {r.eshop_url && <a href={r.eshop_url} target="_blank" rel="noreferrer" className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-400 hover:bg-slate-100" aria-label="e-shop" data-testid={`no-photo-eshop-${r.code}`}><ExternalLink className="h-4 w-4" /></a>}
              <PhotoUpload code={r.code} compact onDone={() => rep.refetch()} />
            </li>
          ))}
          {!rows.length && <li className="px-4 py-6 text-sm text-slate-500" data-testid="no-photo-empty">{t("x.photo.none")}</li>}
        </ul>
      )}
    </div>
  );
}
